"use client";

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ModelConfigEntry } from "@/client/config/models.config";
import type { StartGenerationHandler } from "@/client/types/generation";
import type { QueueStats } from "@/client/hooks/useTaskQueue";
import {
  isCustomFormConfig,
  isSchemaFormConfig,
  type ModelFormProps,
  type PromptUpdateCommand,
} from "./modelFormTypes";
import SchemaFormRenderer from "./schema/SchemaFormRenderer";
import { buildSchemaPayload } from "./schema/buildPayload";
import type { SchemaFieldDefinition, SchemaFormDefinition, SchemaFormValues } from "./schema/types";

type VersionOption = { value: string; label: string };

interface ModelFormRouterProps {
  modelKey: string;
  config?: ModelConfigEntry;
  startGenWithPayload: StartGenerationHandler;
  generating: boolean;
  /** 模型页可传入队列统计信息（可选） */
  queueStats?: QueueStats | null;
  generationError?: string | null;
  generationMessages?: string[];
  taskListNode?: React.ReactNode;
  promptCommand?: PromptUpdateCommand | null;
  onPromptCommandHandled?: () => void;
  externalSubmitRef?: React.MutableRefObject<(() => void) | null> | null;
  onSchemaResolved?: (schema: SchemaFormDefinition<string>) => void;
  controllerRef?: React.MutableRefObject<ModelFormController | null> | null;
  /** 隐藏 SchemaFormRenderer 内置提交按钮，由上层触发 */
  hideSchemaSubmit?: boolean;
  /** 在 SchemaFormRenderer 中使用图标+Tooltip 标签样式（用于 Prompt Dock 等场景） */
  useIconLabels?: boolean;
  /** 若为 true，SchemaFormRenderer 将不渲染内置 Prompt/图片首行，由外层自行处理 */
  externalTopRow?: boolean;
  versionFieldKey?: string;
  onVersionOptionsResolved?: (options: VersionOption[]) => void;
  onCurrentVersionValueResolved?: (value: string) => void;
  /** 若为 true，隐藏 versionFieldKey 对应的字段（用于 PromptDock 已整合版本选择器的场景） */
  hideVersionField?: boolean;
}

export interface ModelFormController {
  kind: "schema" | "custom" | "none";
  getSchema?: () => SchemaFormDefinition<string> | null;
  /** 设置单个字段值（仅 Schema 路径提供） */
  setFieldValue?: (key: string, value: unknown) => void;
  /** 读取单个字段值（仅 Schema 路径提供） */
  getFieldValue?: (key: string) => unknown;
  /** 获取当前 Schema 表单的全部值快照（仅 Schema 路径提供） */
  getAllValues?: () => SchemaFormValues<string> | null;
  /** 监听 Schema 值的变化，返回取消订阅函数 */
  subscribeValueChanges?: (listener: () => void) => () => void;
  /** 读取当前版本字段值（仅 Schema 路径提供） */
  getCurrentVersionValue?: () => string | undefined;
  /** 读取版本字段 options（仅 Schema 路径提供） */
  getVersionOptions?: () => VersionOption[] | null;
  /** 设置版本字段值（仅 Schema 路径提供） */
  setVersionValue?: (value: string) => void;
}

const ModelFormRouter: React.FC<ModelFormRouterProps> = ({
  modelKey,
  config,
  startGenWithPayload,
  generating,
  queueStats,
  generationError,
  generationMessages,
  taskListNode,
  promptCommand,
  onPromptCommandHandled,
  externalSubmitRef,
  onSchemaResolved,
  controllerRef,
  hideSchemaSubmit,
  useIconLabels,
  externalTopRow = false,
  versionFieldKey = "model",
  onVersionOptionsResolved,
  onCurrentVersionValueResolved,
  hideVersionField = false,
}) => {
  const { t } = useTranslation("pages");
  const formConfig = config?.workspace?.form;
  const isCustom = isCustomFormConfig(formConfig);
  const customFormConfig = isCustom ? formConfig : undefined;
  const schemaFormConfig = isSchemaFormConfig(formConfig) ? formConfig : undefined;

  const [customForm, setCustomForm] = useState<React.ComponentType<ModelFormProps<unknown>> | null>(null);
  const [formValues, setFormValues] = useState<unknown>(() =>
    customFormConfig ? customFormConfig.createInitialValues() : undefined
  );
  const formValuesRef = useRef<unknown>(formValues);
  const [formValuesOwner, setFormValuesOwner] = useState<string | null>(() =>
    customFormConfig ? modelKey : null
  );
  const [loadError, setLoadError] = useState<string | null>(null);
  const [buildingPayload, setBuildingPayload] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [schemaDefinition, setSchemaDefinition] = useState<SchemaFormDefinition<string> | null>(null);
  const [schemaLoading, setSchemaLoading] = useState(false);
  const schemaValuesRef = useRef<SchemaFormValues<string> | null>(null);
  const externalFieldVersionRef = useRef(0);
  const schemaValueSubscribersRef = useRef(new Set<() => void>());
  const versionFieldPresenceRef = useRef(false);
  const versionOptionsRef = useRef<VersionOption[] | null>(null);
  const onVersionOptionsResolvedRef = useRef<typeof onVersionOptionsResolved>(onVersionOptionsResolved);
  const onCurrentVersionValueResolvedRef = useRef<typeof onCurrentVersionValueResolved>(
    onCurrentVersionValueResolved
  );
  const lastVersionValueRef = useRef<string | undefined>(undefined);
  const [externalFieldUpdate, setExternalFieldUpdate] = useState<
    { key: string; value: unknown; version: number } | null
  >(null);

  const notifySchemaValueSubscribers = useCallback(() => {
    if (schemaValueSubscribersRef.current.size === 0) {
      return;
    }
    const listeners = Array.from(schemaValueSubscribersRef.current);
    listeners.forEach((listener) => {
      try {
        listener();
      } catch {
        // 忽略订阅器内部错误以保证其它监听器继续执行
      }
    });
  }, []);

  const resolveVersionFieldValue = useCallback(
    (values: SchemaFormValues<string> | null) => {
      if (!values || !versionFieldPresenceRef.current) {
        return undefined;
      }
      const record = values as Record<string, unknown>;
      const raw = record[versionFieldKey];
      return typeof raw === "string" ? raw : undefined;
    },
    [versionFieldKey]
  );

  const updateVersionValueNotification = useCallback((value: string | undefined) => {
    if (value === undefined) {
      if (lastVersionValueRef.current !== undefined) {
        lastVersionValueRef.current = undefined;
      }
      return;
    }
    if (lastVersionValueRef.current === value) {
      return;
    }
    lastVersionValueRef.current = value;
    onCurrentVersionValueResolvedRef.current?.(value);
  }, []);

  const handleSchemaValuesChange = useCallback(
    (nextValues: SchemaFormValues<string>) => {
      schemaValuesRef.current = nextValues;
      const versionValue = resolveVersionFieldValue(nextValues);
      updateVersionValueNotification(versionValue);
      notifySchemaValueSubscribers();
    },
    [notifySchemaValueSubscribers, resolveVersionFieldValue, updateVersionValueNotification, versionFieldKey]
  );


  

  useEffect(() => {
    formValuesRef.current = formValues;
  }, [formValues]);

  


  const extractPromptFromValues = useCallback(
    (values: unknown): string => {
      if (!customFormConfig) return "";
      const typed = values as unknown;
      if (customFormConfig.getPrompt) {
        const result = customFormConfig.getPrompt(typed as never);
        return typeof result === "string" ? result : "";
      }
      if (typed && typeof typed === "object" && typed !== null && "prompt" in typed) {
        const raw = (typed as Record<string, unknown>).prompt;
        return typeof raw === "string" ? raw : "";
      }
      return "";
    },
    [customFormConfig]
  );

  const applyPromptToValues = useCallback(
    (values: unknown, command: PromptUpdateCommand): unknown => {
      if (!customFormConfig) return values;
      const baseValues =
        (values as unknown) ?? (customFormConfig.createInitialValues() as unknown);
      if (customFormConfig.applyPrompt) {
        return customFormConfig.applyPrompt(baseValues as never, command);
      }
      if (baseValues && typeof baseValues === "object") {
        const record = baseValues as Record<string, unknown>;
        const currentPrompt = typeof record.prompt === "string" ? record.prompt : "";
        const nextPrompt =
          command.mode === "append" && currentPrompt.trim()
            ? `${currentPrompt.trim()}\n\n${command.text}`
            : command.text;
        return { ...record, prompt: nextPrompt };
      }
      return baseValues;
    },
    [customFormConfig]
  );

  useEffect(() => {
    if (!customFormConfig) {
      setCustomForm(null);
      setLoadError(null);
      setFormValues(undefined);
      setSubmitError(null);
      setFormValuesOwner(null);
      return;
    }

    setCustomForm(null);
    let cancelled = false;
    const load = async () => {
      try {
        setLoadError(null);
        const mod = await customFormConfig.load();
        if (cancelled) return;
        setCustomForm(() => mod.default);
      } catch (error) {
        if (cancelled) return;
        const message =
          error instanceof Error && error.message
            ? error.message
            : t("modelWorkspace.form.loadError", "无法加载模型表单");
        setLoadError(message);
        setCustomForm(null);
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [customFormConfig, t]);

  // 保持 onSchemaResolved 的稳定引用，避免父组件重渲染导致 effect 反复触发
  const onSchemaResolvedRef = useRef<typeof onSchemaResolved>(onSchemaResolved);
  useEffect(() => {
    onSchemaResolvedRef.current = onSchemaResolved;
  }, [onSchemaResolved]);
  useEffect(() => {
    onVersionOptionsResolvedRef.current = onVersionOptionsResolved;
  }, [onVersionOptionsResolved]);
  useEffect(() => {
    onCurrentVersionValueResolvedRef.current = onCurrentVersionValueResolved;
  }, [onCurrentVersionValueResolved]);

  // 仅在 schemaFormConfig 指针变化时触发加载；同指针下即使父层重渲染也不再进入“加载中”，彻底消除进度圈抖动
  const lastSchemaConfigRef = useRef<typeof schemaFormConfig>(null);
  useEffect(() => {
    if (!schemaFormConfig) {
      setSchemaDefinition(null);
      setSchemaLoading(false);
      lastSchemaConfigRef.current = null;
      return;
    }
    if (lastSchemaConfigRef.current === schemaFormConfig && schemaDefinition) {
      // 已加载且配置未变，不重复触发加载
      return;
    }
    lastSchemaConfigRef.current = schemaFormConfig;
    let cancelled = false;
    const loadSchema = async () => {
      try {
        setLoadError(null);
        setSchemaLoading(true);
        const mod = await schemaFormConfig.load();
        if (cancelled) return;
        const resolved = extractSchemaDefinition(mod);

        setSchemaDefinition(resolved as SchemaFormDefinition<string>);
        onSchemaResolvedRef.current?.(resolved as SchemaFormDefinition<string>);
      } catch (error) {
        if (cancelled) return;
        const message = error instanceof Error && error.message ? error.message : "无法加载模型表单";
        setLoadError(message);
        setSchemaDefinition(null);
      } finally {
        if (!cancelled) {
          setSchemaLoading(false);
        }
      }
    };
    void loadSchema();
    return () => {
      cancelled = true;
    };
  }, [schemaFormConfig, schemaDefinition]);

  useEffect(() => {
    if (!schemaDefinition) {
      versionFieldPresenceRef.current = false;
      versionOptionsRef.current = null;
      updateVersionValueNotification(undefined);
      onVersionOptionsResolvedRef.current?.([]);
      return;
    }
    const versionField = findSchemaFieldByKey(schemaDefinition.fields, versionFieldKey);
    if (!versionField) {
      versionFieldPresenceRef.current = false;
      versionOptionsRef.current = null;
      updateVersionValueNotification(undefined);
      onVersionOptionsResolvedRef.current?.([]);
      return;
    }
    versionFieldPresenceRef.current = true;
    const translateOptionLabel = (key: string, defaultValue?: string) => {
      if (typeof defaultValue === "string") {
        return t(key, defaultValue);
      }
      return t(key);
    };
    const resolvedOptions = extractVersionOptionsFromField(versionField, translateOptionLabel);
    versionOptionsRef.current = resolvedOptions;
    onVersionOptionsResolvedRef.current?.(resolvedOptions);
    let currentVersionValue = resolveVersionFieldValue(schemaValuesRef.current);
    if (currentVersionValue === undefined) {
      const defaults = schemaDefinition.defaults ?? {};
      const fallback = defaults[versionFieldKey];
      currentVersionValue = typeof fallback === "string" ? fallback : undefined;
    }
    updateVersionValueNotification(currentVersionValue);
  }, [
    resolveVersionFieldValue,
    schemaDefinition,
    t,
    updateVersionValueNotification,
    versionFieldKey,
  ]);

  useEffect(() => {
    if (!customFormConfig) {
      return;
    }
    const ownedValues = formValuesRef.current;
    if (formValuesOwner !== modelKey || ownedValues === undefined) {
      const initialValues = customFormConfig.createInitialValues();
      formValuesRef.current = initialValues;
      setFormValues(initialValues);
      setFormValuesOwner(modelKey);
    }
    if (submitError !== null) {
      setSubmitError(null);
    }
  }, [customFormConfig, formValuesOwner, modelKey, submitError]);

  useEffect(() => {
    if (!promptCommand) return;
    if (!customFormConfig) {
      if (!schemaFormConfig) {
        onPromptCommandHandled?.();
      }
      return;
    }
    setFormValues((prev: unknown) => {
      const nextValues = applyPromptToValues(prev ?? customFormConfig.createInitialValues(), promptCommand);
      
      return nextValues;
    });
    setFormValuesOwner(modelKey);
    onPromptCommandHandled?.();
  }, [
    applyPromptToValues,
    customFormConfig,
    modelKey,
    onPromptCommandHandled,
    promptCommand,
    schemaFormConfig,
  ]);

  const handleCustomChange = useCallback((nextValues: unknown) => {
    setFormValues(nextValues);
    setFormValuesOwner(modelKey);
  }, [modelKey]);

  const handleCustomSubmit = useCallback(
    async (values: unknown) => {
      if (!customFormConfig) return;
      try {
        setBuildingPayload(true);
        setSubmitError(null);
        const payload = await customFormConfig.buildPayload(values, { modelKey, values });
        await startGenWithPayload(payload);
      } catch (error) {
        const message =
          error instanceof Error && error.message
            ? error.message
            : t("modelWorkspace.form.submitError", "提交失败，请稍后重试");
        setSubmitError(message);
      } finally {
        setBuildingPayload(false);
      }
    },
    [customFormConfig, modelKey, startGenWithPayload, t]
  );

  const handleSchemaSubmit = useCallback(
    async (values: SchemaFormValues<string>) => {
      if (!schemaDefinition) return;

      const resolveSubmitError = (error: unknown) => {
        const message =
          error instanceof Error && error.message
            ? error.message
            : t("modelWorkspace.form.submitError", "提交失败，请稍后重试");
        setSubmitError(message);
      };

      setSubmitError(null);
      setBuildingPayload(true);

      let payload: Awaited<ReturnType<typeof buildSchemaPayload>> | null = null;
      try {
        payload = await buildSchemaPayload({
          schema: schemaDefinition,
          values,
          modelKey,
        });
      } catch (error) {
        resolveSubmitError(error);
        return;
      } finally {
        setBuildingPayload(false);
      }

      if (!payload) {
        return;
      }

      try {
        const startPromise = startGenWithPayload(payload);
        void startPromise.catch(resolveSubmitError);
      } catch (error) {
        resolveSubmitError(error);
      }
    },
    [schemaDefinition, modelKey, startGenWithPayload, t]
  );

  useEffect(() => {
    if (!externalSubmitRef) {
      return;
    }
    if (!customFormConfig) {
      if (!schemaFormConfig) {
        externalSubmitRef.current = null;
      }
      return;
    }
    externalSubmitRef.current = () => {
      const latestValues = formValuesRef.current ?? customFormConfig.createInitialValues();
      void handleCustomSubmit(latestValues);
    };
    return () => {
      externalSubmitRef.current = null;
    };
  }, [customFormConfig, externalSubmitRef, handleCustomSubmit, schemaFormConfig]);

  const runtimeError = submitError ?? generationError ?? null;
  // 新设计：busy 只表示"正在构建 payload"，不再包含 generating
  // 这样允许多个任务同时进入队列执行
  const busy = buildingPayload;
  void queueStats;
  void generating; // 保留 prop 以备将来使用

  // 计算需要隐藏的字段（当 hideVersionField 为 true 时隐藏 versionFieldKey 对应的字段）
  const hiddenFieldKeys = useMemo(() => {
    if (!hideVersionField || !versionFieldKey) {
      return undefined;
    }
    return new Set<string>([versionFieldKey]);
  }, [hideVersionField, versionFieldKey]);

  // 暴露控制器能力（仅 Schema 路径）
  useEffect(() => {
    if (!controllerRef) return;
    if (!schemaFormConfig) {
      controllerRef.current = { kind: customFormConfig ? "custom" : "none" };
      return;
    }
    const applyFieldValueUpdate = (key: string, value: unknown) => {
      externalFieldVersionRef.current += 1;
      setExternalFieldUpdate({ key, value, version: externalFieldVersionRef.current });
    };
    const readVersionValue = () => resolveVersionFieldValue(schemaValuesRef.current);

    controllerRef.current = {
      kind: "schema",
      getSchema: () => schemaDefinition,
      setFieldValue: applyFieldValueUpdate,
      getFieldValue: (key: string) => {
        const current = schemaValuesRef.current ?? null;
        const value = current ? (current as Record<string, unknown>)[key] : undefined;
        return value;
      },
      getAllValues: () => {
        const current = schemaValuesRef.current;
        return current ? ({ ...current } as SchemaFormValues<string>) : null;
      },
      subscribeValueChanges: (listener: () => void) => {
        if (typeof listener !== "function") {
          return () => {};
        }
        schemaValueSubscribersRef.current.add(listener);
        return () => {
          schemaValueSubscribersRef.current.delete(listener);
        };
      },
      getCurrentVersionValue: () => {
        if (!versionFieldPresenceRef.current) {
          return undefined;
        }
        return readVersionValue();
      },
      getVersionOptions: () => {
        if (!versionFieldPresenceRef.current) {
          return null;
        }
        return versionOptionsRef.current ? [...versionOptionsRef.current] : [];
      },
      setVersionValue: (value: string) => {
        if (!versionFieldPresenceRef.current) {
          return;
        }
        applyFieldValueUpdate(versionFieldKey, value);
      },
    } as ModelFormController;
  }, [
    controllerRef,
    customFormConfig,
    resolveVersionFieldValue,
    schemaDefinition,
    schemaFormConfig,
    versionFieldKey,
  ]);

  if (!customFormConfig && !schemaFormConfig) {
    return (
      <div className="flex justify-center py-4">
        <div className="text-sm text-muted-foreground">
          {t("modelWorkspace.form.unsupported", "该模型暂未配置表单，请稍后重试。")}
        </div>
      </div>
    );
  }

  if (schemaFormConfig) {
    // 只有在完全没有可用 schema 时才显示 loading,避免切换时卸载组件
    if (!schemaDefinition && (schemaLoading || loadError)) {
      if (loadError) {
        return (
          <div className="flex justify-center py-4">
            <div className="text-sm text-destructive">{loadError}</div>
          </div>
        );
      }
      return (
        <div className="flex justify-center py-4">
          <div className="animate-spin size-6 border-2 border-muted-foreground/50 border-t-foreground rounded-full" />
        </div>
      );
    }
    if (!schemaDefinition) {
      return null;
    }
    return (
      <SchemaFormRenderer
        schema={schemaDefinition}
        modelKey={modelKey}
        busy={busy || schemaLoading}
        error={runtimeError}
        messages={generationMessages}
        taskListSlot={taskListNode}
        onSubmit={handleSchemaSubmit}
        promptCommand={promptCommand}
        onPromptCommandHandled={onPromptCommandHandled}
        externalSubmitRef={externalSubmitRef}
        onSchemaResolved={(s) => onSchemaResolved?.(s as SchemaFormDefinition<string>)}
        onValuesChange={handleSchemaValuesChange}
        externalFieldUpdate={externalFieldUpdate as { key: string; value: unknown; version: number } | null}
        hideSubmit={hideSchemaSubmit}
        useIconLabels={useIconLabels}
        externalTopRow={externalTopRow}
        hiddenFieldKeys={hiddenFieldKeys}
      />
    );
  }

  if (!customForm) {
    return (
      <div className="flex justify-center py-4">
        {loadError ? (
          <div className="text-sm text-destructive">{loadError}</div>
        ) : (
          <div className="animate-spin size-6 border-2 border-muted-foreground/50 border-t-foreground rounded-full" />
        )}
      </div>
    );
  }

  if (!customFormConfig) {
    return null;
  }

  const FormComponent = customForm as React.ComponentType<ModelFormProps<unknown>>;
  const valuesForForm =
    formValuesOwner === modelKey && formValues !== undefined
      ? formValues
      : customFormConfig.createInitialValues();

  return (
    <FormComponent
      modelKey={modelKey}
      values={valuesForForm}
      onChange={handleCustomChange}
      onSubmit={handleCustomSubmit}
      busy={busy}
      error={runtimeError}
      messages={generationMessages}
      taskListSlot={taskListNode}
    />
  );
};

export default ModelFormRouter;

const findSchemaFieldByKey = <FieldKey extends string = string>(
  fields: Array<SchemaFieldDefinition<FieldKey>>,
  targetKey: string
): SchemaFieldDefinition<FieldKey> | null => {
  for (const field of fields) {
    if (!field) continue;
    if (field.type === "group") {
      const match = findSchemaFieldByKey(field.children, targetKey);
      if (match) {
        return match;
      }
      continue;
    }
    if (field.type === "divider") {
      continue;
    }
    if (field.key === targetKey) {
      return field;
    }
  }
  return null;
};

const extractVersionOptionsFromField = (
  field: SchemaFieldDefinition<string>,
  translate: (key: string, defaultValue?: string) => string
): VersionOption[] => {
  if (field.type !== "select" && field.type !== "toggle") {
    return [];
  }
  if (!field.options || field.options.kind !== "static") {
    return [];
  }
  return field.options.options
    .map<VersionOption | null>((option) => {
      if (option?.value === undefined || option.value === null) {
        return null;
      }
      const normalizedValue = String(option.value);
      const labelKey = option.labelKey ?? normalizedValue;
      const label = translate(labelKey, labelKey);
      return { value: normalizedValue, label };
    })
    .filter((option): option is VersionOption => Boolean(option));
};

const extractSchemaDefinition = <FieldKey extends string = string>(
  mod:
    | SchemaFormDefinition<FieldKey>
    | { default: SchemaFormDefinition<FieldKey> }
    | { schema: SchemaFormDefinition<FieldKey> }
): SchemaFormDefinition<FieldKey> => {
  if ("schema" in mod && mod.schema) {
    return mod.schema;
  }
  if ("default" in mod && mod.default) {
    return mod.default;
  }
  return mod as SchemaFormDefinition<FieldKey>;
};
